import useSWRInfinite from 'swr/infinite'

import { BookConditions, DeliveryMethods, SellersToBookParams, SellersToBookResponse } from '@hermes/types/'

import { useLocale } from '@hooks/useLocale'
import getRegionFromLocale from '@lib/utils/getRegionFromLocale'
import { useMemo, useState } from 'react'

const apiUrl = String(process.env.NEXT_PUBLIC_HERMES_API_URL)

const resourceUrlPattern = '{apiUrl}/v1/{tenant}/components/sellers-to-book/v2/{bookUuid}'

const fetcher = async (url: string): Promise<SellersToBookResponse> => {
  const response = await fetch(url)
  const data = await response.json()
  if (data?.error) {
    throw new Error('Unable to fetch sellers to book.')
  }
  return data
}

export const useRecommendedSellersV2 = ({
  bookUuid,
  size = 5,
  deliveryMethods = ['shipping', 'both', 'meetup'],
  conditions = []
}: {
  bookUuid: string
  size?: SellersToBookParams['query']['size']
  deliveryMethods?: Array<'shipping' | 'meetup' | 'both'>
  conditions?: string[]
}) => {
  const locale = useLocale()
  const region = getRegionFromLocale(locale)
  const [shippingMethodFilter, setShippingMethodFilter] =
    useState<Array<'shipping' | 'meetup' | 'both'>>(deliveryMethods)
  const [conditionsFilter, setConditionsFilter] = useState<string[]>(conditions)

  const getCacheKey = (pageIndex: number) => {
    // If missing params, then skip fetching by returning null
    if (!bookUuid || !apiUrl || !region) return null

    const queryParameters = {
      page: (pageIndex + 1).toString(),
      size: size.toString(),
      deliveryMethods: shippingMethodFilter.join(','),
      ...(conditionsFilter.length > 0 && { conditions: conditionsFilter.join(',') })
    }
    const query = new URLSearchParams(queryParameters).toString()
    const resource = resourceUrlPattern
      .replace('{apiUrl}', apiUrl)
      .replace('{tenant}', region)
      .replace('{bookUuid}', bookUuid)
    return `${resource}?${query}`
  }

  const {
    data,
    isLoading,
    error,
    size: swrSize,
    setSize
  } = useSWRInfinite(getCacheKey, fetcher, {
    revalidateFirstPage: false
  })

  const results = data?.flatMap((d) => d.data) ?? []

  const hasMore = !!data?.at(-1)?.pagination?.continue

  const resultsByShippingMethod = useMemo(
    () => ({
      all: results,
      shipping: results.filter((i) => ['shipping', 'both'].includes(i.deliveryMethod)),
      meetup: results.filter((i) => ['meetup'].includes(i.deliveryMethod))
    }),
    [results, shippingMethodFilter]
  )

  return {
    data: resultsByShippingMethod,
    isLoading: isLoading || (swrSize > 0 && data && typeof data[swrSize - 1] === 'undefined') || false,
    error,
    hasMore,
    nextPage: () => {
      if (hasMore) {
        setSize(swrSize + 1)
      }
    },
    setFilters: ({
      conditions: newConditions,
      deliveryMethods: newDeliveryMethods
    }: {
      conditions?: BookConditions[]
      deliveryMethods?: DeliveryMethods[]
    }) => {
      let arrayDeliveryMethods: Array<'shipping' | 'meetup' | 'both'> = newDeliveryMethods || shippingMethodFilter
      if (newDeliveryMethods?.includes('shipping')) arrayDeliveryMethods = ['shipping', 'both']
      if (newDeliveryMethods) setShippingMethodFilter(arrayDeliveryMethods)
      if (newConditions) setConditionsFilter(newConditions)
      setSize(1)
    }
  }
}
